import axios from 'axios';
import { ref, computed, onBeforeUnmount } from 'vue';

export default function useQueue({ router = null, route = null, startImmediate = true } = {}) {
    const clientSessionId = ref(localStorage.client_session_id || null);
    const loading = ref(false);

    const url = () => window.location.hostname;
    const ruta = () => (route && route.fullPath) ? route.fullPath : window.location.pathname;

    function isRutaAdmin() {
        const currentPath = window.location.pathname;
        return currentPath.includes('admin') || currentPath.includes('reservas');
    }

    function redirect(session_id) {
        window.location.href = process.env.VUE_APP_SALA_ESPERA_URL + '/?client_id=' + session_id;
    }

    function registrarActividad(pageArg) {
        const admin = isRutaAdmin();
        if (!admin) {
            loading.value = true;
            return axios.post(process.env.VUE_APP_URL_QUEUE_API + '/api/queue/clients/activity', {
                url: url(),
                page: pageArg || ruta(),
                id: clientSessionId.value
            }).then(response => {
                if (response.data.error) {
                    localStorage.client_session_id = null;
                    clientSessionId.value = null;
                    return iniciarAtividad();
                } else {
                    if (response.data.waiting_room != null) {
                        redirect(response.data.session_id);
                    }

                    loading.value = false;
                }
            }).catch(() => {
                loading.value = false;
            });
        }
        return Promise.resolve();
    }

    function iniciarAtividad() {
        const admin = isRutaAdmin();
        if (!admin) {
            loading.value = true;
            return axios.post(process.env.VUE_APP_URL_QUEUE_API + '/api/queue/clients/new-client', {
                url: url(),
                page: ruta()
            }).then(response => {
                clientSessionId.value = response.data.client_id;
                localStorage.client_session_id = clientSessionId.value;

                if (response.data.waiting_room != null) {
                    redirect(response.data.session_id);
                }

                loading.value = false;
            }).catch(() => {
                if (!isRutaAdmin() && clientSessionId.value == null) {
                    if (router) {
                        router.push({ name: 'front.queue', params: { redirect: false } });
                    }
                }
                loading.value = false;
            });
        }
        return Promise.resolve();
    }

    // interval control
    let _interval = null;
    let _afterHook = null;

    function start() {
        if (process.env.VUE_APP_URL_QUEUE_API === 'disabled') return;

        if (localStorage.client_session_id) {
            clientSessionId.value = localStorage.client_session_id;
            registrarActividad();
        } else {
            iniciarAtividad();
        }

        _interval = setInterval(() => {
            registrarActividad();
        }, 45000);

        // listen to route changes via router.afterEach when available
        if (router && typeof router.afterEach === 'function') {
            _afterHook = (to, from) => {
                if (process.env.VUE_APP_URL_QUEUE_API === 'disabled') return;
                if (!loading.value) {
                    // update clientSessionId from localStorage (same behavior as original mixin)
                    clientSessionId.value = localStorage.client_session_id || clientSessionId.value;
                    registrarActividad(to && to.fullPath ? to.fullPath : undefined);
                }
            };
            router.afterEach(_afterHook);
        }
    }

    function stop() {
        if (_interval) {
            clearInterval(_interval);
            _interval = null;
        }
        // remove afterEach hook if possible (Vue Router 3 stores hooks in router.afterHooks)
        if (_afterHook && router && router.afterHooks && Array.isArray(router.afterHooks)) {
            const idx = router.afterHooks.indexOf(_afterHook);
            if (idx !== -1) router.afterHooks.splice(idx, 1);
            _afterHook = null;
        }
    }

    if (startImmediate) start();

    // if used inside setup(), provide automatic cleanup
    try {
        onBeforeUnmount(() => stop());
    } catch (e) {
        // onBeforeUnmount may be a no-op if not in setup; ignore
    }

    return {
        clientSessionId: computed(() => clientSessionId.value),
        loading: computed(() => loading.value),
        start,
        stop,
        registrarActividad,
        iniciarAtividad,
        isRutaAdmin,
        redirect
    };
}
